import { createGui } from '@App/app/entities/layer/create-gui.model';
import { PolygonLines } from '@App/app/entities/layer/polygons/polygon-lines.model';
import { Polygon, ViewerPolygonLayer } from '@App/app/entities/layer/polygons/polygon.model';
import { Injectable } from '@angular/core';
import { AbstractMesh, Mesh, TransformNode, Vector3 } from 'babylonjs';
import { TextBlock } from 'babylonjs-gui';
import {
  CENTER_SPHERES_CONTAINER_NAME,
  PICKER_SPHERES_CONTAINER_NAME,
  POLYGON_NAME,
} from 'src/app/configs/babylon.config';
import { UnitsService } from 'src/app/pages/viewer/services/utils/units.service';
import { toImperialArea, toImperialLength } from 'src/app/shared/utils/units-converter';
import { Coordinates3 } from '../../../../entities/layer/measurements/coordinates';
import { NameTag } from '../../../../entities/layer/name-tag.model';
import { GenerateGUIService } from '../../gui-services/generate-gui-service/generate-gui.service';
import { SceneService } from '../../scene-service/scene.service';
import { PolygonMathService } from './polygon-math.service';
import { PolygonMeshService } from './polygon-mesh.service';

@Injectable({
  providedIn: 'root',
})
export class PolygonUtilsService implements createGui {
  constructor(
    private generateGUIService: GenerateGUIService,
    private polygonMeshService: PolygonMeshService,
    private polygonMathService: PolygonMathService,
    private sceneService: SceneService,
    private unitsService: UnitsService,
  ) {}

  drawPolygon(
    polygon: Polygon,
    data: { points: Coordinates3[]; squareArea: number },
  ): [ViewerPolygonLayer, TransformNode] {
    const newPolygon = polygon as ViewerPolygonLayer;
    const root = new AbstractMesh(`${POLYGON_NAME}${newPolygon.id}`, this.sceneService.scene);
    const centerSpheresContainer = new AbstractMesh(
      CENTER_SPHERES_CONTAINER_NAME,
      this.sceneService.scene,
    );
    const pickerSpheresContainer = new AbstractMesh(
      PICKER_SPHERES_CONTAINER_NAME,
      this.sceneService.scene,
    );
    root.id = `${newPolygon.id}`;

    root.addChild(centerSpheresContainer);
    root.addChild(pickerSpheresContainer);
    newPolygon.pointsMeshes = data.points.map((point) => {
      return this.createSphere(
        new Vector3(point.x, point.y, point.z),
        pickerSpheresContainer,
        false,
      );
    });

    newPolygon.pointsNameTag = newPolygon.pointsMeshes.map((mesh, index) => {
      return this.generateGUIService.createNameTag(`${++index}`, mesh, false);
    });

    this.initPolygon(newPolygon, root);

    if (newPolygon.gizmoManager) {
      newPolygon.gizmoManager.positionGizmoEnabled = false;
    }
    if (newPolygon.squareAreaTag) {
      this.toggleImperialGui(newPolygon.squareAreaTag, newPolygon.polygonLines, data.squareArea);
    }

    return [newPolygon, root];
  }

  createGui(layer: ViewerPolygonLayer): NameTag | undefined {
    return undefined;
  }

  createSphere(position: Vector3, root: TransformNode, visible: boolean) {
    return this.polygonMeshService.createSphere(position, root, visible);
  }

  initPolygon(polygon: ViewerPolygonLayer, root: TransformNode) {
    polygon.mesh = this.polygonMeshService.createPolygonMesh(polygon.pointsMeshes, root);
    polygon.polygonLines = this.createPolygonLinesDescription(
      polygon.mesh,
      polygon.pointsMeshes,
      root,
      true,
    );
    if (polygon.data.squareArea) {
      polygon.squareAreaTag = this.generateGUIService.createSquareAreaTag(
        polygon.mesh,
        polygon.data.squareArea,
        true,
      );
    }
  }

  toggleImperialGui(squareAreaTag: TextBlock, polygonLines: PolygonLines, squareArea: number) {
    const isImperial = this.unitsService.getImperialUnitsActive();
    squareAreaTag.text = isImperial ? `${toImperialArea(squareArea)}` : `${squareArea}m²`;
    polygonLines.polygonsText.forEach((tag, i) => {
      tag.text = isImperial
        ? `${toImperialLength(polygonLines.polygonsLinesLength[i])}`
        : `${polygonLines.polygonsLinesLength[i]}m`;
    });
  }

  createPolygonLinesDescription(
    polygonMesh: Mesh,
    pointsMeshes: Mesh[],
    root: TransformNode,
    visible: boolean,
  ): PolygonLines {
    const positionsArr = this.clonePositions(pointsMeshes);
    const polygonsLinesLength = this.polygonMathService.calculateLineLengths(positionsArr);
    const polygonsLinesCenters = this.polygonMathService.calculateLineCenters(
      positionsArr,
      polygonMesh.position.y,
    );

    const centerSpheresContainer = root
      .getChildren()
      .filter((mesh) => mesh.id === CENTER_SPHERES_CONTAINER_NAME);

    const polygonsSphereCenters = polygonsLinesCenters.map((center) => {
      const centerSphere = this.polygonMeshService.initCenterSphere(center);
      centerSphere.parent = centerSpheresContainer.length !== 0 ? centerSpheresContainer[0] : root;

      return centerSphere;
    });

    const polygonsText = polygonsSphereCenters.map((center, i) =>
      this.generateGUIService.createLengthTag(center, polygonsLinesLength[i], visible),
    );

    return {
      polygonsLinesLength,
      polygonsLinesCenters,
      polygonsSphereCenters,
      polygonsText,
    };
  }

  movePolygonLines(polygon: ViewerPolygonLayer) {
    if (!polygon.mesh) {
      return;
    }
    const positionsArr = this.clonePositions(polygon.pointsMeshes);
    const polygonsLinesCenters = this.polygonMathService.calculateLineCenters(
      positionsArr,
      polygon.mesh.position.y,
    );
    polygon.polygonLines.polygonsSphereCenters.forEach((sphere, i) => {
      sphere.position = polygonsLinesCenters[i];
    });
  }

  movePolygon(polygon: ViewerPolygonLayer, root: TransformNode) {
    polygon.mesh?.dispose();
    delete polygon.mesh;
    polygon.mesh = this.polygonMeshService.createPolygonMesh(polygon.pointsMeshes, root);
    this.movePolygonLines(polygon);
    this.moveSquareAreaTag(polygon);
    if (polygon.squareAreaTag && polygon.data.squareArea) {
      this.toggleImperialGui(polygon.squareAreaTag, polygon.polygonLines, polygon.data.squareArea);
    }
    this.updateGizmoPickables(polygon);
    return polygon;
  }

  updateGizmoPickables(polygon: ViewerPolygonLayer) {
    if (!polygon.gizmoManager) {
      return polygon.gizmoManager;
    }

    polygon.gizmoManager.attachableMeshes = [...polygon.pointsMeshes];
    if (polygon.mesh) {
      polygon.gizmoManager.attachableMeshes.push(polygon.mesh);
    }

    return polygon.gizmoManager;
  }

  moveSquareAreaTag(polygon: ViewerPolygonLayer) {
    polygon.data.squareArea = Polygon.calculateSquareArea(polygon.pointsMeshes);
    if (polygon.mesh) polygon.squareAreaTag?.linkWithMesh(polygon.mesh);
  }

  recomputeRootSpace(polygon: ViewerPolygonLayer, root: TransformNode) {
    const positions = polygon.pointsMeshes.map((mesh) => mesh.position);
    const centroid = this.polygonMathService.calculateCentroid(positions);

    root.position.addInPlace(centroid);
    polygon.pointsMeshes.forEach((mesh) => {
      mesh.position.subtractInPlace(centroid);
    });

    this.movePolygon(polygon, root);

    return { pointsMeshes: polygon.pointsMeshes, root };
  }

  private clonePositions(meshes: Mesh[]): Vector3[] {
    return meshes.map((mesh) => mesh.position.clone());
  }
}
